import React, { useState } from 'react';
import { ShoppingCart, Package, Search, FileText, ChevronDown, ChevronUp } from 'lucide-react';

// Replace with real order data pulled from Supabase or your order management system
const ORDERS = [
  { id: 'NGX-2026-0891', date: 'Feb 24, 2026', items: [{ name: 'Electrodes 2"×2" — Half Case', sku: 'NGX-ELEC-022-HALF', qty: 2, price: 300.00 }], status: 'Delivered', total: 600.00, tracking: '1Z999AA10123456784' },
  { id: 'NGX-2026-0876', date: 'Feb 17, 2026', items: [{ name: 'Locking Cables — 1 Set', sku: 'NGX-LOCK-070-1', qty: 1, price: 350.00 }, { name: 'Bifurcating Cables — 1 Set', sku: 'NGX-BIFC-080-1', qty: 1, price: 200.00 }], status: 'Shipped', total: 550.00, tracking: '1Z999AA10123456785' },
  { id: 'NGX-2026-0855', date: 'Feb 10, 2026', items: [{ name: 'Nerve Health Solution — 12 Pack', sku: 'NGX-SUPP-12', qty: 1, price: 432.00 }, { name: 'Electrodes 2"×4" — Quarter Case', sku: 'NGX-ELEC-039-QTR', qty: 1, price: 120.00 }], status: 'Delivered', total: 552.00, tracking: '1Z999AA10123456780' },
  { id: 'NGX-2026-0831', date: 'Jan 28, 2026', items: [{ name: 'Electrodes 2"×2" — Full Case', sku: 'NGX-ELEC-022-FULL', qty: 1, price: 500.00 }], status: 'Delivered', total: 500.00, tracking: '1Z999AA10123456770' },
  { id: 'NGX-2026-0814', date: 'Jan 15, 2026', items: [{ name: 'NGX Medical-Grade Battery', sku: 'NGX-BATTERY', qty: 1, price: 100.00 }, { name: 'NGX Fuse Set', sku: 'NGX-FUSE', qty: 2, price: 10.00 }], status: 'Delivered', total: 120.00, tracking: '1Z999AA10123456760' },
  { id: 'NGX-2026-0798', date: 'Jan 3, 2026', items: [{ name: 'Nerve Health Solution — 24 Pack', sku: 'NGX-SUPP-24', qty: 1, price: 768.00 }], status: 'Delivered', total: 768.00, tracking: '1Z999AA10123456750' },
  { id: 'NGX-2025-0776', date: 'Dec 18, 2025', items: [{ name: 'Electrodes 2"×2" — Half Case', sku: 'NGX-ELEC-022-HALF', qty: 3, price: 300.00 }], status: 'Delivered', total: 900.00, tracking: '1Z999AA10123456740' },
  { id: 'NGX-2025-0751', date: 'Nov 30, 2025', items: [{ name: 'Locking Cables — 2 Set', sku: 'NGX-LOCK-070-2', qty: 1, price: 650.00 }], status: 'Delivered', total: 650.00, tracking: '1Z999AA10123456730' },
];

const STATUS_BADGE = {
  Delivered: 'badge-active',
  Shipped: 'badge-teal',
  Processing: 'badge-pending',
  Cancelled: 'badge-inactive',
};

export default function OrderHistoryPage({ setCurrentPage }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');

  const filtered = ORDERS.filter(o => {
    const matchSearch = o.id.toLowerCase().includes(search.toLowerCase()) ||
      o.items.some(i => i.name.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = filterStatus === 'All' || o.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalSpend = ORDERS.reduce((s, o) => s + o.total, 0);
  const ytdSpend = ORDERS.filter(o => o.date.includes('2026')).reduce((s, o) => s + o.total, 0);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Order History</div>
          <div className="page-subtitle">All past and current orders for your practice</div>
        </div>
        <button className="btn btn-primary" onClick={() => setCurrentPage('supplies')}>
          <ShoppingCart size={15} />
          Place New Order
        </button>
      </div>

      <div className="page-body">

        {/* SUMMARY STATS */}
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div>
              <div className="stat-label">Total Orders</div>
              <div className="stat-value">{ORDERS.length}</div>
              <div className="stat-sub">All time</div>
            </div>
            <div className="stat-icon blue"><ShoppingCart size={20} /></div>
          </div>
          <div className="stat-card">
            <div>
              <div className="stat-label">YTD Spend</div>
              <div className="stat-value">${ytdSpend.toLocaleString()}</div>
              <div className="stat-sub">Jan – present 2026</div>
            </div>
            <div className="stat-icon orange"><Package size={20} /></div>
          </div>
          <div className="stat-card">
            <div>
              <div className="stat-label">Total Spend</div>
              <div className="stat-value">${totalSpend.toLocaleString()}</div>
              <div className="stat-sub">Lifetime</div>
            </div>
            <div className="stat-icon teal"><FileText size={20} /></div>
          </div>
          <div className="stat-card">
            <div>
              <div className="stat-label">Avg Order Value</div>
              <div className="stat-value">${(totalSpend / ORDERS.length).toFixed(0)}</div>
              <div className="stat-sub">Per order</div>
            </div>
            <div className="stat-icon pink"><ShoppingCart size={20} /></div>
          </div>
        </div>

        {/* FILTERS */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input className="form-input" placeholder="Search orders or products..." style={{ paddingLeft: 32 }} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none', gap: 4 }}>
            {['All', 'Delivered', 'Shipped', 'Processing'].map(s => (
              <button key={s} className={`tab ${filterStatus === s ? 'active' : ''}`} style={{ borderBottom: 'none', padding: '7px 14px' }} onClick={() => setFilterStatus(s)}>{s}</button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{filtered.length} orders</div>
        </div>

        {/* ORDERS TABLE */}
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Items</th>
                <th>Status</th>
                <th>Total</th>
                <th>Tracking</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(order => (
                <>
                  <tr key={order.id} style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === order.id ? null : order.id)}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b', fontWeight: 500 }}>{order.id}</td>
                    <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{order.date}</td>
                    <td style={{ color: '#475569' }}>
                      {order.items.length === 1
                        ? order.items[0].name
                        : <span>{order.items[0].name} <span style={{ color: '#94a3b8', fontSize: 12 }}>+{order.items.length - 1} more</span></span>
                      }
                    </td>
                    <td><span className={`badge ${STATUS_BADGE[order.status]}`}>{order.status}</span></td>
                    <td style={{ fontWeight: 700, color: '#10497c' }}>${order.total.toLocaleString()}</td>
                    <td style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{order.tracking}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {expanded === order.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        Details
                      </button>
                    </td>
                  </tr>

                  {/* EXPANDED ROW */}
                  {expanded === order.id && (
                    <tr key={`${order.id}-detail`}>
                      <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                        <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Order Items</div>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                {['Item', 'SKU', 'Qty', 'Unit Price', 'Subtotal'].map(h => (
                                  <th key={h} style={{ textAlign: 'left', fontSize: 11, color: '#94a3b8', fontWeight: 600, padding: '4px 12px 8px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item, i) => (
                                <tr key={i}>
                                  <td style={{ padding: '6px 12px 6px 0', fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{item.name}</td>
                                  <td style={{ padding: '6px 12px 6px 0', fontSize: 11, fontFamily: 'monospace', color: '#94a3b8' }}>{item.sku}</td>
                                  <td style={{ padding: '6px 12px 6px 0', fontSize: 13, color: '#475569' }}>{item.qty}</td>
                                  <td style={{ padding: '6px 12px 6px 0', fontSize: 13, color: '#475569' }}>${item.price.toFixed(2)}</td>
                                  <td style={{ padding: '6px 12px 6px 0', fontSize: 13, fontWeight: 600, color: '#10497c' }}>${(item.price * item.qty).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', gap: 10 }}>
                              <button className="btn btn-primary btn-sm">Reorder</button>
                              <button className="btn btn-secondary btn-sm">Download Invoice</button>
                            </div>
                            <div style={{ fontWeight: 700, color: '#10497c', fontSize: 15 }}>
                              Order Total: ${order.total.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <ShoppingCart size={40} style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 500 }}>No orders found</div>
          </div>
        )}
      </div>
    </>
  );
}
